import ic_cross from '@/assets/images/icons/cross.svg';
import { Modal } from '@/components/shared';
import { FileUpload } from '@/components/shared/file-upload/FileUpload';
import { UploadedFile } from '@/components/shared/file-upload/types';
import { Order } from '@/types';
import { bem } from '@/utilities';
import dayjs from 'dayjs';
import ja from 'dayjs/locale/ja';
import React, { useEffect, useState } from 'react';
import guidePdf from '@/assets/files/guide/guide_ver5.pdf';

dayjs.locale(ja);

type ParkingType = 'emptySpace' | 'coinParking';
type Props = {
  openModal: boolean;
  order?: Order;
  selectedAvailableTimeframeStart: string;
  selectedAvailableTimeframeEnd: string;
  onClickCloseModal: () => void;
  onClickSubmitButton: (
    contactName: string,
    contactPhoneNumber: string,
    parkingType: string,
    comment: string,
    uploadedFiles: UploadedFile[]
  ) => void;
};

export const OrderRequestModal: React.FC<Props> = (props) => {
  const {
    openModal,
    order,
    selectedAvailableTimeframeStart,
    selectedAvailableTimeframeEnd,
    onClickCloseModal,
    onClickSubmitButton
  } = props;
  const [contactName, setContactName] = useState<string>();
  const [contactPhoneNumber, setContactPhoneNumber] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isConfirmPlace, setIsConfirmPlace] = useState<boolean>(false);
  const [parkingType, setParkingType] = useState<ParkingType>();
  const [comment, setComment] = useState<string>();
  const [isFocusComment, setIsFocusComment] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const contactPhoneNumberRegExp = new RegExp(/^([0-9]|[０-９]|-|ー)*$/);

  useEffect(() => {
    setIsSubmitting(false);
    setIsConfirmPlace(false);
    setParkingType(null);
    setComment(null);
    setUploadedFiles([]);
  }, [openModal]);

  if (!openModal || !order) return <></>;

  const formattedScheduledAtDate = () => {
    return `${dayjs(selectedAvailableTimeframeStart).format('YYYY年MM月DD日（dd） HH時')}〜${dayjs(
      selectedAvailableTimeframeEnd
    ).format('HH時')}`;
  };

  const isValidContactPhoneNumber = () => {
    if (!contactPhoneNumber) return true;
    return contactPhoneNumberRegExp.test(contactPhoneNumber);
  };

  return (
    <Modal isOpen={openModal} onClickOverlay={() => onClickCloseModal()} contentClassModifiers={{ large: true }}>
      <div className={bem('modal', 'close-icon')} onClick={() => onClickCloseModal()}>
        <img src={ic_cross} />
      </div>
      <div className={bem('order-request-modal', 'step')}>
        <div className={bem('order-request-modal', 'step-first')}>内容確認</div>
        <div className={bem('order-request-modal', 'step-bar')} />
        <div className={bem('order-request-modal', 'step-second')}>予約完了</div>
      </div>
      <p className={bem('order-request-modal', 'title')}>予約を確定しますか？</p>
      <p className={bem('order-request-modal', 'schedule')}>{formattedScheduledAtDate()}</p>
      <table className={bem('order-request-modal', 'info')}>
        <tbody>
          <tr>
            <td className={bem('order-request-modal', 'info-label')} valign='top'>
              <p className={bem('order-request-modal', 'car-label')}>車両</p>
            </td>
            <td>
              <p className={bem('order-request-modal', 'car-text')}>{order.ownedCar.carNumber}</p>
              <p className={bem('order-request-modal', 'car-text')}>
                <span>{order.ownedCar.carModelName}</span>
                <span>{order.ownedCar.carModelNumber}</span>
              </p>
            </td>
          </tr>
          <tr>
            <td className={bem('order-request-modal', 'info-label')}>
              <p className={bem('order-request-modal', 'name-label')}>名前</p>
            </td>
            <td>
              <p className={bem('order-request-modal', 'name-text')}>{`${order.userName}様`}</p>
            </td>
          </tr>
          <tr>
            <td className={bem('order-request-modal', 'info-label')}>
              <p className={bem('order-request-modal', 'location-label')}>作業場所住所</p>
            </td>
            <td>
              <p className={bem('order-request-modal', 'location-text')}>{order.location}</p>
            </td>
          </tr>
          <tr>
            <td className={bem('order-request-modal', 'info-label')}>
              <p className={bem('order-request-modal', 'time-label')}>作業目安</p>
            </td>
            <td>
              <p className={bem('order-request-modal', 'time-text')}>{`${order.duration}分`}</p>
            </td>
          </tr>
        </tbody>
      </table>
      <div className={bem('order-request-modal', 'corresponder')}>
        <h4 className={bem('order-request-modal', 'corresponder-title')}>名前</h4>
        <input
          className={bem('order-request-modal', 'input')}
          onChange={(e) => setContactName(e.target.value)}
          placeholder='現地対応者のお名前'
        />
      </div>
      <div className={bem('order-request-modal', 'phone')}>
        <h4 className={bem('order-request-modal', 'phone-title')}>電話番号</h4>
        {!isValidContactPhoneNumber() && (
          <p className={bem('order-request-modal', 'phone-error')}>電話番号を正しく入力してください</p>
        )}
        <input
          className={bem('order-request-modal', 'input')}
          onChange={(e) => setContactPhoneNumber(e.target.value)}
          placeholder='現地対応者のお電話番号'
        />
      </div>
      <div className={bem('order-request-modal', 'place')}>
        <h4 className={bem('order-request-modal', 'place-title')}>
          作業場所の確認 <span>（必須）</span>
        </h4>
        <p className={bem('order-request-modal', 'place-text')}>
          平置きの駐車場や、作業許可のある駐車場にて作業が可能です。
        </p>
        <p className={bem('order-request-modal', 'place-text')}>
          ※機械式の駐車場や他店様の敷地内、路上では作業を行うことができませんのでご注意ください。
        </p>
        <div className={bem('order-request-modal', 'place-input')}>
          <input
            type='checkbox'
            id='order-request-modal-place-checkbox'
            className={bem('order-request-modal', 'place-checkbox')}
            onChange={() => setIsConfirmPlace(!isConfirmPlace)}
          />
          <label htmlFor='order-request-modal-place-checkbox' className={bem('order-request-modal', 'place-label')}>
            作業場所に問題ありません
          </label>
        </div>
      </div>
      <div className={bem('order-request-modal', 'parking')}>
        <h4 className={bem('order-request-modal', 'parking-title')}>
          整備士の車を停める場所の有無 <span>（必須）</span>
        </h4>
        <div className={bem('order-request-modal', 'parking-form')}>
          <div className={bem('order-request-modal', 'parking-input')}>
            <input
              type='radio'
              id='order-request-modal-parking-radio'
              className={bem('order-request-modal', 'parking-radio')}
              value='emptySpace'
              checked={parkingType === 'emptySpace'}
              onChange={(e) => setParkingType(e.target.value as ParkingType)}
            />
            <label htmlFor='order-request-modal-parking-radio' className={bem('order-request-modal', 'parking-label')}>
              あり
            </label>
          </div>
          <div className={bem('order-request-modal', 'parking-input')}>
            <input
              type='radio'
              id='order-request-modal-parking-radio'
              className={bem('order-request-modal', 'parking-radio')}
              value='coinParking'
              checked={parkingType === 'coinParking'}
              onChange={(e) => setParkingType(e.target.value as ParkingType)}
            />
            <label htmlFor='order-request-modal-parking-radio' className={bem('order-request-modal', 'parking-label')}>
              なし（近隣のコインパーキングに止めて伺います）
            </label>
          </div>
        </div>
      </div>
      <div className={bem('order-request-modal', 'notes')}>
        <h4>注意事項</h4>
        <div>
          <p className={bem('order-request-modal', 'notes-text')}>
            作業場所はお客様でご用意願います。路上や機械式駐車場では作業ができません。
          </p>
          <p className={bem('order-request-modal', 'notes-text')}>
            作業当日に発覚した場合はキャンセル費用を頂戴します。
          </p>
        </div>
      </div>
      <div className={bem('order-request-modal', 'comment')}>
        <h4>ご連絡事項</h4>
        <p className={bem('order-request-modal', 'comment-text')}>
          ・作業場所に関する説明や、その他の伝達事項をご記載ください。
        </p>
        <p className={bem('order-request-modal', 'comment-precaution')}>
          ※ 時間の指定はお受けできかねます。確定後お送りするSMSにて内容をご確認ください。
        </p>
        <div className={bem('order-request-modal', 'comment-input-wrapper')}>
          <textarea
            id='order-request-modal__comment'
            className={bem('order-request-modal', 'comment-input')}
            onChange={(e) => setComment(e.target.value)}
            onFocus={() => setIsFocusComment(true)}
            onBlur={() => setIsFocusComment(false)}
          />
          {!isFocusComment && !comment && (
            <p className={bem('order-request-modal', 'comment-placeholder')}>
              例）
              <br />
              ・作業場所が住所から徒歩3分のコインパーキングです
              <br />
              ・当日不在の可能性があり、その際は事務所のスタッフに声かけてください
            </p>
          )}
        </div>
      </div>
      <div className={bem('order-request-modal', 'file-upload')}>
        <h4>参考資料の添付</h4>
        <p className={bem('order-request-modal', 'file-upload-text')}>
          作業場所の詳細がわかる画像やPDFを添付できます。
        </p>
        <FileUpload
          onFilesChange={setUploadedFiles}
          maxFiles={5}
          acceptedTypes={['image/*', 'application/pdf']}
          disabled={isSubmitting}
        />
      </div>
      <div className={bem('order-request-modal', 'button-bubble')}>確定すると、日時が確定されます</div>
      <button
        className={bem('order-request-modal', 'close-button')}
        onClick={() => {
          setIsSubmitting(true);
          onClickSubmitButton(contactName, contactPhoneNumber, parkingType, comment, uploadedFiles);
        }}
        disabled={isSubmitting || !isConfirmPlace || !parkingType || !isValidContactPhoneNumber()}
      >
        確定
      </button>
      <p className={bem('order-request-modal', 'supplement')}>
        不明点がある場合は、
        <a href={guidePdf} className={bem('order-request-modal', 'supplement-link')} target='_blank' rel='noreferrer'>
          よくある質問
        </a>
        を確認してください
      </p>
    </Modal>
  );
};
